import { useState, useEffect, useCallback } from "react";
// import { useContext } from "react";

import PropTypes from "prop-types";

//import Components
import RenderChart from "../../charts/RenderChart";

function AddProAnalysisChart({ productsSecondaryData = [], column2 = [], chartType = "", isTop2BoxValuesShown = true }) {
    const [imageHeight, setImageHeight] = useState(100);
    const [inputMinValue, setInputMinValue] = useState(0);
    const [inputMaxValue, setInputMaxValue] = useState(7);
    const [currentZoom, setCurrentZoom] = useState({ start: 0, end: 100 });

    // sort the data

    // Sorting array by macroCategory and add sample size
    productsSecondaryData = productsSecondaryData.map((datum) => {
        const obj = { ...datum };
        obj["title"] = `${datum.title} / ${datum.study_reference_title}`;
        return obj;
    });

    //Sorting displayLabels array alphabetically within each object based on displayLabel
    const sortedColumn2 = column2;

    const checkOtherProductsNA = (data, backendVariable) => {
        let noOfProducts = data.length;
        let productsScoreWithNA = 0;
        data.map((datum) => {
            const _score = datum.scores.filter((ele) => ele.backendVariable === backendVariable);
            if (isTop2BoxValuesShown && (!_score[0] || !_score[0].agr_per)) {
                productsScoreWithNA += 1;
            }
            else if (!isTop2BoxValuesShown && (!_score[0] || !_score[0].mean)) {
                productsScoreWithNA += 1;
            }
        });
        if (productsScoreWithNA < noOfProducts) {
            return false;
        } else {
            return true;
        }
    };

    const getYAxisMaxValue = (value) => {
        if (isTop2BoxValuesShown) {
            return 100; // For claims and agreement analysis, if top 2 box values are shown, return 100
        } else {
            if (value <= 5) {
                return 5; // If value <= 5, return 5
            } else if (value > 5 && value <= 7) {
                return 7;
            } else if (value > 7 && value <= 10) {
                return 10;
            } else if (value > 10 && value <= 20) {
                return 20;
            } else if (value > 20 && value <= 50) {
                return 50;
            } else if (value > 50 && value <= 100) {
                return 100;
            }
        }
    };

    const _xAxisLabel = sortedColumn2.map((cols) => {
        if (!checkOtherProductsNA(productsSecondaryData, cols.backendVariable)) {
            return `${cols.displayLabel}`;
        }
    });

    let xAxisLabel = _xAxisLabel.flat();
    xAxisLabel = xAxisLabel.filter((ele) => ele !== undefined);
    xAxisLabel = xAxisLabel.map((label) => label.charAt(0).toUpperCase() + label.slice(1));

    let rich = [];
    let yAxisMaxValue = 5;
    let productsShow = productsSecondaryData.map((datum) => {
        const array = [];
        array.push(datum.title);
        sortedColumn2.flat().forEach((col) => {
            const _score = datum.scores.filter((ele) => ele.backendVariable === col.backendVariable);
            // Calculate the maximum Y-axis value based on the score
            const scoreValue = _score?.[0]?.mean;
            if (scoreValue) {
                yAxisMaxValue = Math.max(yAxisMaxValue, getYAxisMaxValue(scoreValue));
            }

            if (_score?.[0] && isTop2BoxValuesShown && _score?.[0].agr_per) {
                array.push(_score?.[0].agr_per);
            } else if (_score?.[0] && _score?.[0].mean && !isTop2BoxValuesShown) {
                array.push(_score?.[0].mean);
            } else {
                if (!checkOtherProductsNA(productsSecondaryData, col.backendVariable)) {
                    if (isTop2BoxValuesShown) {
                        array.push("NA");
                    } else {
                        array.push(0); // 0 pushed so that in graph lines are continuous
                    }
                }
            }
        });
        return array;
    });
    let values = productsShow.filter((ele) => ele !== undefined);
    const _series = productsSecondaryData.map(() => {
        return {
            type: chartType,
            smooth: true,
            seriesLayoutBy: "row",
        };
    });

    const updateAxisValues = useCallback(() => {
        setInputMaxValue(yAxisMaxValue);
    }, [yAxisMaxValue]);

    useEffect(() => {
        updateAxisValues();
    }, [updateAxisValues]);

    const updateValuesPosition = () => {
        const textElements = document.querySelectorAll("text");

        textElements.forEach((element) => {
            const value = parseFloat(element.textContent);

            if (value < 30) {
                // Get the current transform matrix as a string
                const matrix = element.getAttribute("transform");

                if (matrix && matrix.startsWith("matrix(")) {
                    // Extract the matrix values
                    const matrixValues = matrix.slice(7, -1).split(",").map(Number);
                    matrixValues[5] -= 44;

                    const newMatrix = `matrix(${matrixValues.join(",")})`;
                    // set the new matrix to transform attribute
                    element.setAttribute("transform", newMatrix);
                }
            }
        });
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            updateValuesPosition();
        }, 1500);

        return () => clearTimeout(timeout);
    }, [column2, currentZoom, inputMaxValue, inputMaxValue]);

    const handleInputChange = (e, type) => {
        if (type === "max") {
            if (e.target.value <= yAxisMaxValue && e.target.value >= 0) {
                const updatedValue = (parseFloat(e.target.value) * 100) / yAxisMaxValue;
                setInputMaxValue(e.target.value);
                setCurrentZoom({ start: currentZoom.start, end: updatedValue });
            }
        } else {
            if (e.target.value >= 0 && e.target.value <= yAxisMaxValue) {
                const updatedValue = (parseFloat(e.target.value) * 100) / yAxisMaxValue;
                setInputMinValue(e.target.value);
                setCurrentZoom({ start: updatedValue, end: currentZoom.end });
            }
        }
    };

    const handleDataZoom = useCallback(
        (params) => {
            const zoomData = params.batch ? params.batch[0] : params;
            if (zoomData?.start !== undefined && zoomData?.end !== undefined) {
                const newMin = ((zoomData?.start / 100) * yAxisMaxValue).toFixed(2);
                const newMax = ((zoomData?.end / 100) * yAxisMaxValue).toFixed(2);
                setInputMinValue(newMin);
                setInputMaxValue(newMax);
                setCurrentZoom({ start: zoomData.start, end: zoomData.end });
            }
        },
        [yAxisMaxValue]
    );

    const option = {
        label: {
            position: "insideTop",
            distance: 12,
            fontSize: "auto",
            // show: chartType === "bar",
            show: chartType === "bar" && xAxisLabel.length < 17,
            rotate: 90,
            align: "center",
            verticalAlign: "middle",
            offset: [-14, 0],
            color: "#333",
            textBorderColor: "transparent",
            textBorderWidth: 0,
        },
        dataset: {
            source: [["type", ...xAxisLabel], ...values], // source is 2D array
        },
        dataZoom: [
            {
                type: "slider",
                yAxisIndex: 0,
                orient: "vertical",
                width: 15,
                left: "6%",
                filterMode: "none",
                start: currentZoom.start,
                end: currentZoom.end,
                rangeMode: ["value", "value"],
                // startValue: inputMinValue,
                // endValue: inputMaxValue,
                realtime: false,
            },
            {
                type: "inside",
                yAxisIndex: 0,
                start: currentZoom.start,
                end: currentZoom.end,
                rangeMode: ["value", "value"],
                // startValue: inputMinValue,
                // endValue: inputMaxValue,
            },
        ],
        series: _series,
        legend: {
            icon: "roundRect",
            itemGap: 16,
            top: -5,
            left: "center",
            width: "90%",
            height: 50,
            type: "scroll", // Enable scrolling for the legend
            orient: "horizontal",
            formatter: function (name) {
                const product = productsSecondaryData.find((datum) => datum.title === name);
                if (product) {
                    return `${name} {sampleSize| (n=${product.sampleSize})}`;
                }
                return name;
            },
        },
        tooltip: {
            trigger: "axis",
            valueFormatter: (value) => Number(value).toFixed(2),
            formatter: function (params) {
                let tooltipText = `${params[0].axisValue?.split("/-/")?.[0] || params[0].axisValue}<br/>`;

                params.forEach((item) => {
                    const data = item.data;
                    const dimensionNames = item.dimensionNames;
                    const index = dimensionNames.indexOf(item.seriesName);
                    let score = "NA";
                    if (index !== -1 && data[index] !== 0) {
                        score = data[index];
                    }

                    tooltipText += `
                    <div 
                        style="display: flex;justify-content: space-between;"
                    >
                        <div>${item.marker} ${item.seriesName} </div>
                        <div style="font-weight:bold; padding-left: 20px">${score}</div>
                    </div>`;

                    // tooltipText += `${item.marker} <img style={{width: 14,marginRight: 10,}} src=${WeightToggleIcon} /> ${item.seriesName} <span style="float:right; font-weight:bold; padding-left:20px;">${score}</span><br/>`;
                });
                return tooltipText;
            },
        },
        grid: {
            show: true,
            containLabel: true,
            tooltip: {
                show: true,
            },
            left: "10%",
            right: "5%",
            // top: 30 + values.length*4
            top: "12%",
        },
        // toolbox: {
        //     show: true,
        //     orient: "vertical",
        //     right: "7%",
        //     top: "8%",
        //     feature: {
        //         magicType: {
        //             type: [chartsTypesOptions[altChartType]],
        //         },
        //         restore: {},
        //         saveAsImage: {},
        //     },
        // },
        xAxis: {
            show: true,
            type: "category",
            // data: xAxisLabel,
            // boundaryGap: true,
            axisTick: {
                // show: true,
                alignWithLabel: true,
            },
            axisLabel: {
                // formatter: (value, index) => `{${index}|${value?.split("/-/")?.[0] || value}}`,
                rich: rich,
                textStyle: {
                    rich: {
                        bold: {
                            fontWeight: "900", // Bold style for text with asterisks
                            fontSize: "14px",
                        },
                    },
                },
                rotate: 90,
                align: "right",
                // fontWeight: "bold",
                // backgroundColor: "grey",
                // color: "white",
                width: 400,
                // overflow: "break",
                padding: [1, 5],
                fontFamily: "poppinsregular",
            },
        },
        yAxis: {
            type: "value",
            max: yAxisMaxValue,
        },
    };

    const getOnlyImageHeight = useCallback((height) => {
        setImageHeight(height + 120);
    }, []);

    return (
        <div style={{ position: "relative", marginBottom: "40px" }}>
            <div
                style={{
                    position: "absolute",
                    left: "5%",
                    top: "55px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: `${imageHeight}px`,
                }}
            >
                <span
                    style={{
                        opacity: 1,
                        zIndex: 1,
                        display: "flex",
                        width: "50px",
                        flexDirection: "column",
                    }}
                >
                    <label
                        htmlFor="max-value"
                        style={{
                            fontSize: "10px",
                            textAlign: "center",
                        }}
                    >
                        Max
                    </label>
                    <input
                        className="input_border_color"
                        id="max-value"
                        type="number"
                        value={inputMaxValue}
                        onChange={(e) => handleInputChange(e, "max")}
                        step="0.1"
                        min={inputMinValue + 0.1}
                        max={yAxisMaxValue}
                        style={{
                            fontSize: "10px",
                            textAlign: "center",
                        }}
                    />
                </span>
                <span
                    style={{
                        opacity: 1,
                        zIndex: 1,
                        display: "flex",
                        width: "50px",
                        flexDirection: "column",
                    }}
                >
                    <input
                        className="input_border_color"
                        id="min-value"
                        type="number"
                        value={inputMinValue}
                        onChange={(e) => handleInputChange(e, "min")}
                        step="0.1"
                        min="0.0"
                        max={inputMaxValue - 0.1}
                        style={{
                            fontSize: "10px",
                            textAlign: "center",
                        }}
                    />
                    <label
                        htmlFor="min-value"
                        style={{
                            fontSize: "10px",
                            textAlign: "center",
                        }}
                    >
                        Min
                    </label>
                </span>
            </div>
            <RenderChart
                option={option}
                onEvents={{
                    dataZoom: handleDataZoom,
                }}
                getOnlyImageHeight={getOnlyImageHeight}
                addPro={true}
            />
        </div>
    );
}

// Typechecking props for the AddProAnalysisChart
AddProAnalysisChart.propTypes = {
    productsSecondaryData: PropTypes.array,
    column2: PropTypes.array,
    chartType: PropTypes.string,
    isTop2BoxValuesShown: PropTypes.bool,
};
export default AddProAnalysisChart;
